import React, { useState, useRef, useCallback } from "react";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import WeightInputGroup from "../../components/calculators/WeightInputGroup";
import { useHeightInput, useWeightInput } from "../../hooks/calculatorHooks";
import { getWeightInKg } from "../../utils/weightConverter";
import useErrorFocus from "../../hooks/useErrorFocus";
import bsacalc_formula from "./images/body_surface_area_transparent.png";
import styles from "../../components/calculators/calculator.module.scss";
import { calculateBodySurfaceArea } from "../../utils/calculators/bsa";
import { rounding } from "../../utils/calculators/general";
import { isNumber } from "../../utils/isNumber";
import { validateHeightFromInputs, validateWeightFromInputs } from "../../utils/calculators/validation";
import { getHeightInCm } from "../../utils/lengthConverter";

const BsaCalcApp = ({ isWebLayout = false }) => {
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const resultsRef = useRef(null);
  
  const fieldOrder = ['height', 'weight'];  
  
  useErrorFocus(errors, fieldOrder);

  const {
    heightValue: height,
    heightUnit,
    feetValue: feet,
    inchesValue: inches,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleHeightUnitChange,
    clearHeightFields,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  const {
    weightValue: weight,
    weightUnit,
    handleWeightChange,
    handleWeightUnitChange,
    clearWeightFields,
    unitOptions: weightUnits,
  } = useWeightInput();

  const wrappedHeightChange = useCallback((e) => {
    handleHeightChange(e, setErrors);
  }, [handleHeightChange]);

  const wrappedFeetChange = useCallback((e) => {
    handleFeetChange(e, setErrors);
  }, [handleFeetChange]);

  const wrappedInchesChange = useCallback((e) => {
    handleInchesChange(e, setErrors);
  }, [handleInchesChange]);

  const wrappedUnitChange = useCallback((e) => {
    handleHeightUnitChange(e, setErrors);
  }, [handleHeightUnitChange]);

  const wrappedWeightChange = useCallback((e) => {
    handleWeightChange(e, setErrors);
  }, [handleWeightChange]);

  const wrappedWeightUnitChange = useCallback((e) => {
    handleWeightUnitChange(e, setErrors);
  }, [handleWeightUnitChange]);

  function validateFields() {
    let fieldErrors = {};

    
    const heightError = validateHeightFromInputs(heightUnit, height, feet, inches);
    if (heightError) {
      fieldErrors.height = heightError;
    }

    const weightError = validateWeightFromInputs(weight, weightUnit);
    if (weightError) {
      fieldErrors.weight = weightError;
    }
    
    return fieldErrors;
  }

  function handleClear() {
    clearHeightFields();
    clearWeightFields();
    setResult(null);
    setErrors({});
    setShowResult(false);
  }

  function handleCalculate(e) {
    e.preventDefault();
    const fieldErrors = validateFields();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setShowResult(false);
      setResult(null);
      return;
    }
    const heightInCm = getHeightInCm(heightUnit, height, feet, inches);
    const weightInKg = getWeightInKg(weight, weightUnit);
    const bsa = calculateBodySurfaceArea({
      heightCm: heightInCm,
      weightKg: weightInKg,
    });
    setResult(isNumber(bsa) ? rounding(bsa, 2) : null);
    setShowResult(true);
    setErrors({});
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <HeightInputGroup
        idPrefix="bsa"
        height={height}
        heightUnit={heightUnit}
        feet={feet}
        inches={inches}
        onHeightChange={wrappedHeightChange}
        onFeetChange={wrappedFeetChange}
        onInchesChange={wrappedInchesChange}
        onUnitChange={wrappedUnitChange}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
        data-testid="height-input"
      />
      <WeightInputGroup
        idPrefix="bsa"
        weightValue={weight}
        weightUnit={weightUnit}
        onWeightChange={wrappedWeightChange}
        onWeightUnitChange={wrappedWeightUnitChange}
        error={errors.weight}
        unitOptions={weightUnits}
        isWebLayout={isWebLayout}
        data-testid="weight-input"
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const results = (
    <CalculatorResults
      label="Body surface area"
      value={isNumber(result) ? result.toFixed(2) : ""}
      unit={
        isNumber(result) ? (
          <span className={styles.resultUnit}>
            m<sup>2</sup>
          </span>
        ) : undefined
      }
      showResult={showResult}
    />
  );

  const formulaBlock = (
    <div className={isWebLayout ? "" : styles.formulaBlock}>
      <img
        src={bsacalc_formula}
        alt="Body Surface Area formula"
        className={isWebLayout ? "" : styles.formulaImage}
      />
    </div>
  );

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      {!isWebLayout && (
        <>
          Enter the patient details into the calculator below.
          <br />
        </>
      )}
      The body surface area is calculated using the following formula:
    </div>
  );

  const calculatorTitle = "Body surface area calculator";

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      formulaSection={formulaBlock}
      panel={panel}
      results={<div ref={resultsRef}>{results}</div>}
      isWebLayout={isWebLayout}
    />
  );
};

export default BsaCalcApp;
